import { MatchInfo, RankingPPAPlayerFlat } from '@/modules/players/v2/tabs/ratings-old/types';
import { PpaRanking } from '@/types/ranking.types';
import { RankingStatusBadgeEnum } from '@/types/types';

export type ModuleType = 'tournament' | 'teamLeague' | 'league' | 'club';

export type ModuleNoResultsMessages = {
  singular: string;
  plural: string;
  createAction: string;
  exploreAction: string;
  exploreUrl?: string;
  createUrl?: string;
};

export interface PlatformKey {
  id: number;
  value: string;
}

export interface Platform {
  id: number;
  name: string;
  domain: string;
  apiKey: PlatformKey | undefined;
  users: User[];
  isActive: boolean;
  rolePermissions: number;
}

export interface Country {
  id: number;
  abbreviation: string;
  title: string;
  internationalCountryCallingCode: string;
  stateTitle: string;
  zipCodeTitle: string;
  systemTextNumberId: number;
  abbreviation2Digit: string;
  displayOrder: number;
  isEnabled: boolean;
  regionType: string;
}

export interface State {
  id: number;
  abbreviation: string;
  title: string;
  countryId: number;
  isEnabled: boolean;
}

export interface UserKey {
  id: number;
  value: string;
  isHash: boolean;
}

export interface GoverningBody {
  id: number;
  name: string;
  shortName: string;
  isActive: boolean;
  isSearchFilter: boolean;
  filterSearchParam: string;
  displayOrder: number;
  url: string;
  clubId: string;
  logoUrl: string;
}

export interface UsersPlatforms {
  id: number;
  name: string;
  platformUserUuid: string;
}

export interface UsersGoverningBodies {
  id: number;
  name: string;
  shortName: string;
  memberNumber: string;
}

/** Raw timezone shape as returned by the BE (V2 lookup_timezones). */
export interface BETimezone {
  id: string;
  utcOffset: string;
  name: string;
  abbreviation: string;
  fullName: string;
  hasDst: boolean;
  ianaTzIdentifier: string;
  daylightChange: string | null;
  nonDaylightChange: string | null;
  daylightSavingAbbreviation: string;
  daylightSavingUtcOffset: string;
  displayName: string;
}

/** Normalized timezone shape consumed by the FE. */
export interface Timezone {
  id: string;
  utcOffset: number;
  name: string;
  abbreviation: string;
  fullName: string;
  hasDst: boolean;
  ianaTzIdentifier: string;
  daylightChange: string | undefined;
  nonDaylightChange: string | undefined;
  daylightSavingAbbreviation: string;
  daylightSavingUtcOffset: number;
  displayName: string;
}

export interface Rating {
  id: number;
  title: string;
  from: number;
  to: number;
  selfStartPoint: number;
  hierarchy: number;
  isEnabled: boolean;
}

export interface SponsorImage {
  uuid: string;
  sponsorUuid: string;
  url: string;
  displayOrder: number;
  dateDisabled: Date | undefined;
  isPrimary: boolean;
  isApproved: boolean;
  size: string;
  color: string;
  title: string;
  comments: SystemApprovalComment[];
  totalApprovals: number;
  isPending: boolean;
}

export interface SystemApprovalComment {
  uuid: string;
  systemApprovalItemUuid: string;
  content: string;
  dateCreated: Date | undefined;
  lastModifiedDate: Date | undefined;
  creatorFirstName: string;
  creatorLastName: string;
  creatorUuid: string;
}

export interface UserSponsor {
  uuid: string;
  userUuid: string;
  title: string;
  url: string;
  description: string;
  displayOrder: number;
  dateCreated: Date | undefined;
  dateStartedSponsorship: Date | undefined;
  dateDisabled: Date | undefined;
  isPrimary: boolean;
  isApproved: boolean;
  sponsorImages: SponsorImage[];
  comments: SystemApprovalComment[];
  totalApprovals: number;
  isPending: boolean;
}

export interface UserImage {
  uuid: string;
  userUuid: string;
  type: string;
  url: string;
  dateCreated: Date | undefined;
  firstName: string;
  lastName: string;
  totalApprovals: number;
  dateOfRejection: Date | undefined;
  reasonOfRejection: string;
  dateApproved: Date | undefined;
  comments: SystemApprovalComment[];
}

export interface UserBiography {
  uuid: string;
  userUuid: string;
  bioText: string;
  dateCreated: Date | undefined;
  firstName: string;
  lastName: string;
  totalApprovals: number;
  dateOfRejection: Date | undefined;
  reasonOfRejection: string;
  dateApproved: Date | undefined;
  comments: SystemApprovalComment[];
}

export interface ProLevel {
  id: number;
  title: string;
  displayOrder: number;
  isEnabled: boolean;
  isPro: boolean;
}

export interface User {
  id: number;
  uuid: string;
  platformUuid: string;
  platform: Platform | undefined;
  firstName: string;
  middleName: string;
  lastName: string;
  nameSuffix: string;
  gender: string;
  dob: Date | undefined;
  address1: string;
  address2: string;
  country: Country | undefined;
  state: State | undefined;
  city: string;
  zip: string;
  altAddress1: string;
  altAddress2: string;
  altCity: string;
  altZip: string;
  phone: string;
  sendTextAlerts: boolean;
  sendTextAlertsDate: Date | undefined;
  email: string;
  emergencyContactName: string;
  emergencyContactPhone: string;
  passwordHash: UserKey | undefined;
  profileImage: string;
  nickname: string;
  facebook: string;
  snapchat: string;
  twitter: string;
  youtube: string;
  instagram: string;
  linikedIn: string;
  dateCreated: Date | undefined;
  createdByFullName: string;
  dateUpdated: Date | undefined;
  updatedByFullName: string;
  dateDisabled: Date | undefined;
  disabledByFullName: string;
  phoneAreaCode: string;
  emergencyContactPhoneAreaCode: string;
  languageFamilyId: number;
  handed: string;
  heightFeet: number;
  heightInches: number;
  showAgeToPublic: boolean;
  unsubscribeEmarketingPickleball: boolean;
  unsubscribeEmarketingPickleballSms: boolean;
  unsubscribeEmarketing3rdParty: boolean;
  dateClaimed: Date | undefined;
  /** Keeps information about user IDs across all platforms */
  platformsList: Platform[];
  governingBodiesList: GoverningBody[];
  timezone: Timezone | undefined;
  /** Helpers and wrappers */
  platforms: UsersPlatforms[];
  governingBodies: UsersGoverningBodies[];
  /** Alt country and state */
  altCountry: Country | undefined;
  altState: State | undefined;
  /** Followers */
  totalFollowers: number;
  totalFollowing: number;
  isSubRecord: boolean;
  isTest: boolean;
  isCompleted: boolean;
  /** Country codes for phones */
  emergencyContactPhoneCountryId: number;
  phoneCountryId: number;
  /** ModuleIDs parts */
  createdForModuleUuid: string;
  claimedType: string;
  /** Ratings */
  selfSkillDoubles: Rating | undefined;
  selfSkillMixed: Rating | undefined;
  selfSkillSingles: Rating | undefined;
  selfSkillSkinnySingles: Rating | undefined;
  /** Club */
  primaryClubUuid: string;
  /** User profile ranking */
  pickleballUSAPAUTPRSingles: number;
  pickleballUSAPAUTPRSkinnySingles: number;
  pickleballUSAPAUTPRDoubles: number;
  pickleballUSAPAUTPRMixed: number;
  /** Admin fields */
  isSuperAdmin: boolean;
  isSuperAdminFinancialAccess: boolean;
  isSuperAdminCanManageAdmins: boolean;
  /** Monitoring fields */
  systemDateCreated: Date | undefined;
  systemDateUpdated: Date | undefined;
  originPlatformId: number;
  updateFromPlatformName: string;
  worldPickleballRatingSingles: number;
  worldPickleballRatingDoubles: number;
  worldPickleballRatingMixed: number;
  systemRatingDoubles?: number | null;
  systemRatingSingles?: number | null;
  systemRatingMixed?: number | null;
  systemRatingSkinnySingles?: number | null;
  geniusSportsSinglesUuid: string | undefined;
  duprId: string;
  duprDoubles: number;
  duprSingles: number;
  tiktok: string;
  showCityToPublic: boolean;
  showStateToPublic: boolean;
  showOnlyFirstInitialOfLastNameToPublic: boolean;
  /** int64 number_of_played_games = 104; */
  proLevelId: number;
  singleRating: number | undefined;
  doublesRating: number | undefined;
  mixedRating: number | undefined;
  skinnySinglesRating: number | undefined;
  numberOfPlayedSingleGames: number | undefined;
  numberOfPlayedDoublesGames: number | undefined;
  numberOfPlayedMixedGames: number | undefined;
  numberOfPlayedSkinnySinglesGames: number | undefined;
  isGeniusSportsAdminMember: boolean;
  /** Comma separated values for Platform IDs Admin access */
  pls: string;
  mergedWithUuid: string;
  bio: string;
  sponsorImg: string;
  featuredImg: string;
  featuredVideo: string;
  representingCountry: Country | undefined;
  turnedPro: Date | undefined;
  canConfirmContent: boolean;
  canCreateContent: boolean;
  sponsors: UserSponsor[];
  images: UserImage[];
  biography: UserBiography[];
  pickleballUsapaNumber: string;
  pickleballCanadaNumber: string;
  pickleballLifetimeNumber: string;
  pickleballSsipaNumber: string;
  pickleballPaaNumber: string;
  rolePermissions: number;
  mappedPermissions: string[];
  pbUuid: string;
  ratingsReliabilitySingles: number;
  ratingsReliabilitySkinnySingles: number;
  ratingsReliabilityDoubles: number;
  ratingsReliabilityMixed: number;
  slug: string;
  dateMerged: Date | undefined;
  createdFromPlatformId: number;
  latitude: number;
  longitude: number;
  alternateLatitude: number;
  alternateLongitude: number;
  bodyWeight: number;
  backhand: string;
  coachFirstName: string;
  coachLastName: string;
  showMiddleNameToPublic: boolean;
  hideBackToTop: boolean;
  hideSupportChat: boolean;
  hideQuickLaunchButtons: boolean;
  lastActiveDate: Date | string;
  stripeCustomerId: string;
  duprDateLinkUpdated: string | null;
  socialLoginId: string;
  socialProvider: string | null;
  phoneNumberCallingCode: string;
  emergencyContactCallingCode: string;
  duprData: GetDuprPlayerDataRes;
  isChildAccount?: boolean;
  /** Server-side minor flag from the user GET. Source of truth for minor-only rules. */
  isMinor?: boolean;
  createdByParent?: boolean;
  canLogin: boolean;
  hideOnSearch: boolean;
  ppaRanking: PpaRanking;
  isProPlayer: boolean;
}

export interface GetDuprPlayerDataRes {
  firstName?: string;
  middleName?: string;
  lastName?: string;
  userId: string;
  proLevelId: number;
  duprId: string;
  duprSingles: number;
  duprDoubles: number;
  userSlug: string;
  duprDateLinkUpdated: string | null;
}

export type EntitlementType = 'tournaments' | 'leagues' | 'clubs' | 'profiles';
export type EntitlementTypeLevel = 'FREE_L1' | 'PREMIUM_L1';
export type EntitlementValue = string[];
export type Entitlements = Partial<Record<EntitlementType, EntitlementValue>>;
export interface GetDuprMembershipDataRes {
  entitlements: Entitlements;
  redirectUrl: string;
}

export type Follower = {
  uuid: string;
  firstName: string;
  lastName: string;
  profileImage: string;
  nameSuffix: string;
  state: string;
  country: string;
};

export interface IFollowers {
  users: Follower[];
}
export interface OptionExample {
  id: number;
  label: string;
  value: boolean;
}
export interface BracketFormatOption {
  label: string;
  formatTitle: string;
}
export interface PlayerListData {
  uuid: string;
  slug: string;
  fullName: string;
  gender: string;
  age: string | undefined;
  fullCountry: string;
  location: string;
  country?: string;
  systemActiveDoublesRating: string;
  systemActiveSinglesRating: string;
  systemActiveSkinnySinglesRating: string;
  duprId: string;
  profileImage: string | undefined;
  playerLevel: number;
  countryFlag: string;
  ppaDoublesPoints: number;
  ppaMixedDoublesPoints: number;
  ppaSinglesPoints: number;
  ppaDoublesRank: string;
  ppaMixedDoublesRank: string;
  ppaSinglesRank: string;
  wprBadgeRank?: number;
  wprBadgeTitle?: RankingStatusBadgeEnum;
  wprShowBadgeTitle?: boolean;
}
export interface PlayerData {
  uuid: string;
  firstName: string;
  middleName: string;
  lastNameAndSuffix: string;
  gender: string;
  age: string | undefined;
  fullCountry: string;
  country: string;
  countryFlag: string;
  resides: string | undefined;
  nickname: string;
  facebook: string;
  snapchat: string;
  twitter: string;
  youtube: string;
  instagram: string;
  linikedIn: string;
  handed: string;
  height: string;
  totalFollowers: number;
  totalFollowing: number;
  tiktok: string;
  proLevelId: number;
  systemActiveDoublesRating: string;
  systemActiveSinglesRating: string;
  systemActiveMixedRating: string;
  systemActiveSkinnySinglesRating: string;
  /** WPR (World Pickleball Rating) doubles rating, formatted (e.g. "5.418" or "--"). */
  worldPickleballRatingDoubles: string;
  /** WPR (World Pickleball Rating) singles rating, formatted (e.g. "5.020" or "--"). */
  worldPickleballRatingSingles: string;
  duprDoubles: string;
  duprSingles: string;
  duprId: string;
  featuredImg: string;
  featuredVideo: string;
  turnedPro: string | undefined;
  sponsors: UserSponsor[];
  images: UserImage[];
  biography: UserBiography[];
  pbUuid: string;
  slug: string;
  coach: string | undefined;
  typeOfPlayer?: string | undefined;
  badgeRank?: number;
  badgeTitle?: RankingStatusBadgeEnum;
  showBadgeTitle?: boolean;
}

export interface PlayerLiveGameStatusData {
  EventTitle: string;
  EventID: string;
  ActivityID: string;
  ActivityTitle: string;
  NoMedalWasAwarded: boolean;
  GoldMedal_Team: string;
  SilverMedal_Team: string;
  BronzeMedal_Team: string;
  DateOfActivity: string;
  GoLiveStatusID: number;
  T1_P1_UserID: string;
  T1_P2_UserID: string;
  T2_P1_UserID: string;
  T2_P2_UserID: string;
  T1_P1_FirstName: string;
  T1_P2_FirstName: string;
  T2_P1_FirstName: string;
  T2_P2_FirstName: string;
  T1_P1_LastName: string;
  T1_P2_LastName: string;
  T2_P1_LastName: string;
  T2_P2_LastName: string;
  T1_P1_NameSuffix: string;
  T1_P2_NameSuffix: string;
  T2_P1_NameSuffix: string;
  T2_P2_NameSuffix: string;
  T1_P2_Gender: string;
  T2_P2_Gender: string;
  DidCancelMatch: string;
  DateFinish: string;
  CourtTitle: string;
  DateStart: string;
  DidPrintNextAvailable_DatePrinted: string;
  TeamID_1: string;
  TeamID_Winner: string;
  DisplayMatchNumber: string;
  TeamID_Looser: string;
  TeamID_2: string;
  InBracketType: string;
  TeamID_1_Game1Score: number;
  TeamID_2_Game1Score: number;
  TeamID_1_Game2Score: number;
  TeamID_2_Game2Score: number;
  TeamID_1_Game3Score: number;
  TeamID_2_Game3Score: number;
  TeamID_1_Game4Score: number;
  TeamID_2_Game4Score: number;
  TeamID_1_Game5Score: number;
  TeamID_2_Game5Score: number;
  TeamID_1_Game1DidForfeit: number;
  TeamID_1_Game2DidForfeit: number;
  TeamID_1_Game3DidForfeit: number;
  TeamID_1_Game4DidForfeit: number;
  TeamID_1_Game5DidForfeit: number;
  TeamID_2_Game1DidForfeit: number;
  TeamID_2_Game2DidForfeit: number;
  TeamID_2_Game3DidForfeit: number;
  TeamID_2_Game4DidForfeit: number;
  TeamID_2_Game5DidForfeit: number;
  ScoreID: string;
  ShowActivityDraws: boolean;
}

export const playerLiveGameStatusTemplate: PlayerLiveGameStatusData = {
  EventID: '',
  ActivityID: '',
  ActivityTitle: '',
  NoMedalWasAwarded: false,
  GoldMedal_Team: '',
  SilverMedal_Team: '',
  BronzeMedal_Team: '',
  DateOfActivity: '',
  GoLiveStatusID: 0,
  T1_P1_UserID: '',
  T1_P2_UserID: '',
  T2_P1_UserID: '',
  T2_P2_UserID: '',
  T1_P1_FirstName: '',
  T1_P2_FirstName: '',
  T2_P1_FirstName: '',
  T2_P2_FirstName: '',
  T1_P1_LastName: '',
  T1_P2_LastName: '',
  T2_P1_LastName: '',
  T2_P2_LastName: '',
  T1_P1_NameSuffix: '',
  T1_P2_NameSuffix: '',
  T2_P1_NameSuffix: '',
  T2_P2_NameSuffix: '',
  T1_P2_Gender: '',
  T2_P2_Gender: '',
  DidCancelMatch: '',
  DateFinish: '',
  CourtTitle: '',
  DateStart: '',
  DidPrintNextAvailable_DatePrinted: '',
  TeamID_1: '',
  TeamID_Winner: '',
  DisplayMatchNumber: '',
  TeamID_Looser: '',
  TeamID_2: '',
  InBracketType: '',
  TeamID_1_Game1Score: 0,
  TeamID_2_Game1Score: 0,
  TeamID_1_Game2Score: 0,
  TeamID_2_Game2Score: 0,
  TeamID_1_Game3Score: 0,
  TeamID_2_Game3Score: 0,
  TeamID_1_Game4Score: 0,
  TeamID_2_Game4Score: 0,
  TeamID_1_Game5Score: 0,
  TeamID_2_Game5Score: 0,
  TeamID_1_Game1DidForfeit: 0,
  TeamID_1_Game2DidForfeit: 0,
  TeamID_1_Game3DidForfeit: 0,
  TeamID_1_Game4DidForfeit: 0,
  TeamID_1_Game5DidForfeit: 0,
  TeamID_2_Game1DidForfeit: 0,
  TeamID_2_Game2DidForfeit: 0,
  TeamID_2_Game3DidForfeit: 0,
  TeamID_2_Game4DidForfeit: 0,
  TeamID_2_Game5DidForfeit: 0,
  ScoreID: '',
  EventTitle: '',
  ShowActivityDraws: false,
};

export interface PlayerDataV2 {
  uuid: string;
  email: string;
  phone: string;
  slug: string;
  firstName: string;
  middleName: string;
  lastName: string;
  showAgeToPublic: boolean;
  dob: string;
  proLevelId: number;
  showOnlyFirstInitialOfLastNameToPublic: boolean;
  gender: string;
  showCityToPublic: boolean;
  city: string;
  singlesRating: number;
  doublesRating: number;
  mixedRating: number;
  skinnySinglesRating: number;
  worldPickleballRatingSingles: number;
  worldPickleballRatingDoubles: number;
  worldPickleballRatingMixed: number;
  showStateToPublic: boolean;
  nameSuffix: string;
  countryId: number;
  countryTitle: string;
  countryAbbreviation: string;
  countryAbbreviation2Digit: string;
  representingCountryId: number;
  representingCountryTitle: string;
  representingCountryAbbreviation: string;
  representingCountryAbbreviation2Digit: string;
  profilePictureUrl: string;
  stateId: number;
  stateTitle: string;
  stateAbbreviation: string;
  single365Rank: string;
  doubles365Rank: string;
  mixedDoubles365Rank: string;
  single365Points: number;
  doubles365Points: number;
  mixedDoubles365Points: number;
  duprDoubles: number;
  duprSingles: number;
  duprId: string;
  showMiddleNameToPublic: boolean;
  duprDateLinkUpdated: string;
  selfSkillDoubles: {
    from: number;
  };
  selfSkillSingles: {
    from: number;
  };
  selfSkillMixed: {
    from: number;
  };
  selfSkillSkinnySingles: {
    from: number;
  };
  systemRatingDoubles?: number | null;
  systemRatingSingles?: number | null;
  systemRatingMixed?: number | null;
  systemRatingSkinnySingles?: number | null;
  turnedPro: Date | undefined;
  heightFeet: number;
  heightInches: number;
  handed: string | undefined;
  bodyWeight: number;
  wprBadgeTitle?: RankingStatusBadgeEnum;
  wprBadgeRank?: number | null;
  wprShowBadgeTitle?: boolean;
}

export interface LoginHistoryData {
  id: string;
  timestamp: Date;
  ipAddress: string;
  userAgent: string;
  browser: string;
  browserVersion: string;
  deviceModel: string;
  appName: string;
  /**
   * Full name of the user who performed the login on the player's behalf, or
   * `null` when the player logged in themselves (requesterUuid === player uuid).
   */
  impersonator: string | null;
}

export interface LoginHistoryDataRes {
  id: string;
  userId: string;
  tokenId: string;
  platformId: string;
  timestamp: Date;
  ipAddress: string;
  userAgent: string;
  browser: string;
  browserVersion: string;
  serverMachineName: string;
  requesterUuid: string;
  requesterFirstName: string;
  requesterLastName: string;
  device: string;
  deviceOs: string;
  deviceModel: string | null;
  productId: string;
  clientId: string;
  deviceId: string;
  isTokenActive: boolean;
  appName: string | null;
}

export interface PasswordHistoryData {
  id: string;
  date: Date;
  ipAddress: string;
  serverMachineName: string;
  browser: string;
  browserVersion: string;
  requesterFirstName: string;
  requesterLastName: string;
  userAgent: string;
}

export interface PlayerVideosData {
  teamOnePlayerOneFirstName: string;
  teamOnePlayerOneLastName: string;
  teamOnePlayerOneNameSuffix: string;
  teamOnePlayerTwoFirstName: string;
  teamOnePlayerTwoLastName: string;
  teamOnePlayerTwoNameSuffix: string;
  teamTwoPlayerOneFirstName: string;
  teamTwoPlayerOneLastName: string;
  teamTwoPlayerOneNameSuffix: string;
  teamTwoPlayerTwoFirstName: string;
  teamTwoPlayerTwoLastName: string;
  teamTwoPlayerTwoNameSuffix: string;
  link: string;
}

export interface PlayerVideosTournaments {
  tournamentUuid: string;
  tournamentTitle: string;
}

export interface PlayerVideosByTournament {
  tournamentTitle: string;
  highlights: PlayerVideosData[];
}

export interface PlayerOrganizationsData {
  isActive: boolean;
  doublesRating: number;
  singlesRating: number;
  skinnyRating: number;
}

export interface AddStatusMembershipData {
  didMemberCheck: boolean;
  didMemberCheckPass: boolean;
}

export interface StatusMembershipValidationData {
  didMemberCheck: boolean;
  didMemberCheckPass: boolean;
  failedRedirectURL: string;
  whatFailed: string;
  clubId?: string;
}

export interface PlayerMatchesStats {
  UserUuid: string;
  FullName: string;
  SinglesWon: number;
  SinglesLost: number;
  DoublesWon: number;
  DoublesLost: number;
  MixWon: number;
  MixLost: number;
  SinglesGold: number;
  SinglesSilver: number;
  SinglesBronze: number;
  SinglesApperances: number;
  DoublesGold: number;
  DoublesSilver: number;
  DoublesBronze: number;
  DoublesApperances: number;
  MixGold: number;
  MixSilver: number;
  MixBronze: number;
  MixApperances: number;
  DoublesGSApperances: number;
  DoublesBApperances: number;
  MixGSApperances: number;
  MixBApperances: number;
  SinglesGSApperances: number;
  SinglesBApperances: number;
  GoldGamesPlayed: number;
  MixLongestWinStreak: number;
  SinglesLongestWinStreak: number;
  DoublesLongestWinStreak: number;
  SkinnySinglesLongestWinStreak: number;
  SkinnySinglesWon: number;
  SkinnySinglesLost: number;
  SkinnySinglesGold: number;
  SkinnySinglesSilver: number;
  SkinnySinglesBronze: number;
  SkinnySinglesApperances: number;
  SkinnySinglesGSApperances: number;
  SkinnySinglesBApperances: number;
  MixBestWin: MatchInfo | undefined;
  SinglesBestWin: MatchInfo | undefined;
  DoublesBestWin: MatchInfo | undefined;
  SkinnySinglesBestWin: MatchInfo | undefined;
  MixUnderdogWins: number;
  SinglesUnderdogWins: number;
  DoublesUnderdogWins: number;
  SkinnySinglesUnderdogWins: number;
  MixLastTwoMatches: MatchInfo[];
  DoublesLastTwoMatches: MatchInfo[];
  SinglesLastTwoMatches: MatchInfo[];
  SkinnySinglesLastTwoMatches: MatchInfo[];
  MixCurrentWinStreak: number;
  SinglesCurrentWinStreak: number;
  DoublesCurrentWinStreak: number;
  SkinnySinglesCurrentWinStreak: number;
  MixUnderdogLoss: number;
  SinglesUnderdogLoss: number;
  DoublesUnderdogLoss: number;
  SkinnySinglesUnderdogLoss: number;
  MixIndoorLost: number;
  SinglesIndoorLost: number;
  DoublesIndoorLost: number;
  SkinnySinglesIndoorLost: number;
  MixOutdoorWins: number;
  SinglesOutdoorWins: number;
  DoublesOutdoorWins: number;
  SkinnySinglesOutdoorWins: number;
  MixOutdoorLost: number;
  SinglesOutdoorLost: number;
  DoublesOutdoorLost: number;
  SkinnySinglesOutdoorLost: number;
  MixAfterWinningFirstGameWins: number;
  SinglesAfterWinningFirstGameWins: number;
  DoublesAfterWinningFirstGameWins: number;
  SkinnySinglesAfterWinningFirstGameWins: number;
  MixAfterWinningFirstGameLost: number;
  SinglesAfterWinningFirstGameLost: number;
  DoublesAfterWinningFirstGameLost: number;
  SkinnySinglesAfterWinningFirstGameLost: number;
  MixAfterLosingFirstGameWins: number;
  SinglesAfterLosingFirstGameWins: number;
  DoublesAfterLosingFirstGameWins: number;
  SkinnySinglesAfterLosingFirstGameWins: number;
  MixAfterLosingFirstGameLost: number;
  SinglesAfterLosingFirstGameLost: number;
  DoublesAfterLosingFirstGameLost: number;
  SkinnySinglesAfterLosingFirstGameLost: number;
  MixVSLeftHandWins: number;
  SinglesVSLeftHandWins: number;
  DoublesVSLeftHandWins: number;
  SkinnySinglesVSLeftHandWins: number;
  MixVSLeftHandLost: number;
  SinglesVSLeftHandLost: number;
  DoublesVSLeftHandLost: number;
  SkinnySinglesVSLeftHandLost: number;
  MixVSRightHandWins: number;
  SinglesVSRightHandWins: number;
  DoublesVSRightHandWins: number;
  SkinnySinglesVSRightHandWins: number;
  MixVSRightHandLost: number;
  SinglesVSRightHandLost: number;
  DoublesVSRightHandLost: number;
  SkinnySinglesVSRightHandLost: number;
  MixIndoorWins: number;
  SinglesIndoorWins: number;
  DoublesIndoorWins: number;
  SkinnySinglesIndoorWins: number;
  MixDecidingSetWins: number;
  SinglesDecidingSetWins: number;
  DoublesDecidingSetWins: number;
  SkinnySinglesDecidingSetWins: number;
  MixDecidingSetLost: number;
  SinglesDecidingSetLost: number;
  DoublesDecidingSetLost: number;
  SkinnySinglesDecidingSetLost: number;
  TotalPointsWonDoubles: number;
  TotalPointsLostDoubles: number;
  TotalGamesWonDoubles: number;
  TotalGamesLostDoubles: number;
  TotalPointsWonMix: number;
  TotalPointsLostMix: number;
  TotalGamesWonMix: number;
  TotalGamesLostMix: number;
  TotalPointsWonSingles: number;
  TotalPointsLostSingles: number;
  TotalGamesWonSingles: number;
  TotalGamesLostSingles: number;
  TotalPointsWonSkinnySingles: number;
  TotalPointsLostSkinnySingles: number;
  TotalGamesWonSkinnySingles: number;
  TotalGamesLostSkinnySingles: number;
  GamesWon: number;
  GamesPlayed: number;
  GoldMedals: number;
  SilverMedals: number;
  BronzeMedals: number;
  DoublesBronzeLost: number;
  MixBronzeLost: number;
  SinglesBronzeLost: number;
  SkinnySinglesBronzeLost: number;
  /**
   * Semifinal appearances, returned by /users/{uuid}/matches_stats.
   * `Semifinalist` is the all-divisions total; the prefixed fields are per division.
   */
  Semifinalist?: number;
  DoublesSemifinalist?: number;
  MixSemifinalist?: number;
  SinglesSemifinalist?: number;
  SkinnySinglesSemifinalist?: number;
}

/** One medal the player took in a single event of a tournament. */
export interface MedalEvent {
  EventUuid: string;
  EventTitle: string;
  Medal: 'Gold' | 'Silver' | 'Bronze' | 'FourthPlace' | 'Semifinalist';
}

export type MedalEventDivision = 'Singles' | 'Doubles' | 'Mix' | 'SkinnySingles';

export interface PlayerTournamentStats {
  TournamentName: string;
  DoublesApperances: number;
  SinglesApperances: number;
  MixApperances: number;
  SkinnySinglesApperances: number;
  DoublesGold: number;
  SinglesGold: number;
  MixGold: number;
  SkinnySinglesGold: number;
  DoublesSilver: number;
  SinglesSilver: number;
  MixSilver: number;
  SkinnySinglesSilver: number;
  DoublesBronze: number;
  SinglesBronze: number;
  MixBronze: number;
  SkinnySinglesBronze: number;
  DoublesBronzeLost: number;
  SinglesBronzeLost: number;
  MixBronzeLost: number;
  SkinnySinglesBronzeLost: number;
  TournamentDate: Date | undefined;
  EventUuid: string;
  /** Absent until BE ships it - callers must handle it being undefined. */
  MedalEvents?: Record<MedalEventDivision, MedalEvent[]>;
}

export interface PlayerTournamentStatMap {
  PlayerTournamentStats: { [key: string]: PlayerTournamentStats };
}

export interface OpponentPlayerMatchesStats {
  UserUuid: string;
  FullName: string;
  FullNamePlayerTwo: string;
  GoldGamesPlayed: number;
  GoldMatchesPlayed: number;
  GoldMatchesWon: number;
  BronzeMatchesWon: number;
  GamesPlayed: number;
  MatchesPlayed: number;
  MatchesWon: number;
  GoldGamesWon: number;
  GamesWon: number;
}

export interface PartnerPlayerMatchesStats {
  UserUuid: string;
  FullName: string;
  GoldGamesPlayed: number;
  GoldMatchesPlayed: number;
  GoldMatchesWon: number;
  MatchesPlayed: number;
  MatchesWon: number;
  GoldGamesWon: number;
  BronzeMatchesWon: number;
  BronzeMatchesPlayed: number;
  GamesPlayed: number;
  GamesWon: number;
  StatsVsOpponent: { [key: string]: OpponentPlayerMatchesStats };
}

export interface CalculatedTournamentStats {
  TournamentsOneMedal: number;
  TournamentsTwoMedals: number;
  TournamentsThreeGoldMedals: number;
  TournamentsThreeGoldMedalsStreak: number;
}

export interface CumulativeOpponentPlayerMatchesStatsShard {
  GoldGamesPlayed: number;
  GoldMatchesPlayed: number;
  GoldMatchesWon: number;
  BronzeMatchesWon: number;
  GamesPlayed: number;
  MatchesPlayed: number;
  MatchesWon: number;
  GoldGamesWon: number;
  GamesWon: number;
}

export interface CumulativeOpponentPlayerMatchesStats {
  OpponentUserUuid: string;
  OpponentFullName: string;
  SinglesStats: CumulativeOpponentPlayerMatchesStatsShard | undefined;
  SkinnySinglesStats: CumulativeOpponentPlayerMatchesStatsShard | undefined;
  DoublesStats: CumulativeOpponentPlayerMatchesStatsShard | undefined;
  MixedStats: CumulativeOpponentPlayerMatchesStatsShard | undefined;
  AllStats: CumulativeOpponentPlayerMatchesStatsShard | undefined;
}

export interface PlayerMatchesFullStats {
  PlayerStats: PlayerMatchesStats | undefined;
  PlayerTournamentStats: { [key: number]: PlayerTournamentStatMap };
  /** Single stats */
  StatsVsOpponent: { [key: string]: OpponentPlayerMatchesStats };
  StatsVsOpponentSkinnySingles: { [key: string]: OpponentPlayerMatchesStats };
  StatsWithPartnerMix: { [key: string]: PartnerPlayerMatchesStats };
  StatsWithPartnerDoubles: { [key: string]: PartnerPlayerMatchesStats };
  StatsPerYear: { [key: number]: PlayerMatchesStats };
  CalculatedTournamentStats: CalculatedTournamentStats | undefined;
  CumulativeOpponentStats: { [key: string]: CumulativeOpponentPlayerMatchesStats };
  CacheKey: string;
  CacheTimestamp: number;
}

export interface ReadCountry {
  id: number;
  abbreviation: string;
  title: string;
  abbreviation2Digit: string;
}

export interface ReadUser {
  uuid: string;
  platformUuid: string;
  firstName: string;
  middleName: string;
  lastName: string;
  nameSuffix: string;
  gender: string;
  dob: Date | undefined;
  address1: string;
  address2: string;
  country: ReadCountry | undefined;
  state: State | undefined;
  city: string;
  zip: string;
  altAddress1: string;
  altAddress2: string;
  altCity: string;
  altZip: string;
  phone: string;
  sendTextAlerts: boolean;
  sendTextAlertsDate: Date | undefined;
  email: string;
  emergencyContactName: string;
  emergencyContactPhone: string;
  profileImage: string;
  nickname: string;
  facebook: string;
  snapchat: string;
  twitter: string;
  youtube: string;
  instagram: string;
  linikedIn: string;
  dateCreated: Date | undefined;
  createdByFullName: string;
  dateUpdated: Date | undefined;
  updatedByFullName: string;
  dateDisabled: Date | undefined;
  disabledByFullName: string;
  phoneAreaCode: string;
  emergencyContactPhoneAreaCode: string;
  languageFamilyId: number;
  handed: string;
  heightFeet: number;
  heightInches: number;
  showAgeToPublic: boolean;
  unsubscribeEmarketingPickleball: boolean;
  unsubscribeEmarketingPickleballSms: boolean;
  unsubscribeEmarketing3rdParty: boolean;
  dateClaimed: Date | undefined;
  timezone: Timezone | undefined;
  /** Helpers and wrappers */
  platforms: UsersPlatforms[];
  governingBodies: UsersGoverningBodies[];
  /** Alt country and state */
  altCountry: ReadCountry | undefined;
  altState: State | undefined;
  /** Followers */
  totalFollowers: number;
  totalFollowing: number;
  isSubRecord: boolean;
  isTest: boolean;
  isCompleted: boolean;
  /** Country codes for phones */
  emergencyContactPhoneCountryId: number;
  phoneCountryId: number;
  /** ModuleIDs parts */
  createdForModuleUuid: string;
  /** Ratings */
  selfSkillDoublesId: number;
  selfSkillMixedId: number;
  selfSkillSinglesId: number;
  selfSkillSkinnySinglesId: number;
  /** Club */
  primaryClubUuid: string;
  /** User profile ranking */
  pickleballUSAPAUTPRSingles: number;
  pickleballUSAPAUTPRSkinnySingles: number;
  pickleballUSAPAUTPRDoubles: number;
  pickleballUSAPAUTPRMixed: number;
  worldPickleballRatingSingles: number;
  worldPickleballRatingDoubles: number;
  worldPickleballRatingMixed: number;
  worldPickleballRatingSkinnySingles: number;
  systemRatingDoubles?: number | null;
  systemRatingSingles?: number | null;
  systemRatingMixed?: number | null;
  systemRatingSkinnySingles?: number | null;
  geniusSportsSinglesUuid: string;
  duprId: string;
  duprDoubles: number;
  duprSingles: number;
  tiktok: string;
  showCityToPublic: boolean;
  showStateToPublic: boolean;
  showOnlyFirstInitialOfLastNameToPublic: boolean;
  proLevelId: number;
  singleRating: number;
  doublesRating: number;
  mixedRating: number;
  skinnySinglesRating: number;
  numberOfPlayedSingleGames: number;
  numberOfPlayedDoublesGames: number;
  numberOfPlayedMixedGames: number;
  numberOfPlayedSkinnySinglesGames: number;
  /** Comma separated values */
  pls: string;
  isAttendee: boolean;
  bio: string;
  sponsorImg: string;
  featuredImg: string;
  featuredVideo: string;
  representingCountry: ReadCountry | undefined;
  turnedPro: Date | undefined;
  canConfirmContent: boolean;
  canCreateContent: boolean;
  sponsors: UserSponsor[];
  images: UserImage[];
  biography: UserBiography[];
  /** Self-skills ful objects */
  selfSkillDoubles: Rating | undefined;
  selfSkillMixed: Rating | undefined;
  selfSkillSingles: Rating | undefined;
  selfSkillSkinnySingles: Rating | undefined;
  pickleballUsapaNumber: string;
  pickleballCanadaNumber: string;
  pickleballLifetimeNumber: string;
  pickleballSsipaNumber: string;
  pickleballPaaNumber: string;
  isFollowedByUser: boolean;
  pbUuid: string;
  ratingsReliabilitySingles: number;
  ratingsReliabilitySkinnySingles: number;
  ratingsReliabilityDoubles: number;
  ratingsReliabilityMixed: number;
  slug: string;
  ppaRanking: RankingPPAPlayerFlat | undefined;
  dateMerged: Date | undefined;
  latitude: number;
  longitude: number;
  alternateLatitude: number;
  alternateLongitude: number;
  bodyWeight: number;
  backhand: string;
  coachFirstName: string;
  coachLastName: string;
  showMiddleNameToPublic: boolean;
  duprDateLinkUpdated: string;
  playerRank?: number | null;
  badgeTitle?: RankingStatusBadgeEnum;
  showBadgeTitle?: boolean;
}

export interface UserSubscriptions {
  uuid: string;
  gatewaySubscriptionId: string;
  gatewaySubscriptionScheduleId: string;
  amount: number;
  moduleFor: string;
  moduleUuid: string;
  moduleTitle: string;
  productUuid: string;
  membershipPlanFeeUuid: string;
  productType: string;
  userUuid: string;
  status: SubscriptionStatus | number;
  gateway: string;
  dateCreated: string;
  dateUpdated: string;
  dateDeleted: string | null;
  datePeriodStart: string;
  datePeriodEnd: string;
  renewalPaymentMethod: string;
  automaticPaymentWillBeChargedTo: string;
  gatewaySubscriptionRaw: string;
  billedCount: string;
  currency: string;
  membershipPlanFeeScheduleCurrentUuid: string | null;
  membershipPlanAdditionalFeeRecurringCurrentUuids: string | null;
  metadataDeserialized: string | null;
  metadata: string;
  description: string;
  cancelAtPeriodEnd: boolean;
  minimumCyclesToBill: number;
  billingInterval: SubscriptionBillingInterval | number;
}

// Billing platform of an active GameMaker subscription. 'web' is self-manageable
// via the Customer Portal; 'apple'/'google' are managed in the GameMaker mobile app.
export type GameMakerSubStore = 'web' | 'apple' | 'google';

// The logged-in user's active GameMaker subscription, if any. Web subs carry a
// Customer Portal link; store subs expose which store they were bought on.
export interface GameMakerSubscription {
  hasActiveSub: boolean;
  plan: string | null;
  status: string | null;
  currentPeriodEndsAt: number | null;
  store: GameMakerSubStore | null;
  storeLabel: string | null;
  managementUrl: string | null;
}

export interface SystemWaiverVersion {
  uuid: string;
  systemWaiverUuid: string;
  moduleUuid: string;
  dateCreated: string;
  waiverTitle: string;
  waiverUrl: string;
}

export interface SystemWaiverHistory {
  uuid: string;
  userUuid: string;
  moduleUuid: string;
  moduleFor: string;
  beneficiaryModuleFor: string;
  systemWaiverUuid: string;
  systemWaiverVersionUuid: string;
  acceptedBeneficiaryCount: number;
  acceptedBeneficiaryIds: string[];
  pendingBeneficiaryIds: string[];
  totalBeneficiaryCount: number;
  dateAccepted: string;
}

export interface SystemWaiver {
  uuid: string;
  moduleFor: string;
  moduleUuid: string;
  url: string;
  isPrimary: boolean;
  waiverTypeId: number;
  dateCreated: string;
  dateUpdated: string;
  dateDisabled: string | null;
  title: string;
  isActive: boolean;
  systemWaiverVersion: SystemWaiverVersion[];
  systemWaiverModuleHistory: SystemWaiverHistory;
  createdByUuid: string | null;
  updatedByUuid: string | null;
  createdByFullName: string | null;
  updatedByFullName: string | null;
  beneficiaryEnabled: boolean;
}

export interface WaiverByType {
  waiverType: string;
  waivers: SystemWaiver[];
}

export interface WaiverUserDetails {
  firstName: string;
  lastName: string;
  email: string;
  phoneCallingCode: string;
  phone: string;
  uuid: string;
}
export interface SystemWaiverResultData {
  waiversByType: WaiverByType[];
  userInformation: WaiverUserDetails;
}

export enum FeeInterval {
  NON_RECURRING = 'SUBSCRIPTION_BILLING_INTERVAL_NON_RECURRING',
  MONTHLY = 'SUBSCRIPTION_BILLING_INTERVAL_MONTHLY',
  QUARTERLY = 'SUBSCRIPTION_BILLING_INTERVAL_QUARTERLY',
  ANNUALLY = 'SUBSCRIPTION_BILLING_INTERVAL_ANNUALLY',
  BI_ANNUALY = 'SUBSCRIPTION_BILLING_INTERVAL_BI_ANNUALY',
  DAILY = 'SUBSCRIPTION_BILLING_INTERVAL_DAY_PASS',
}

export interface UserV2 {
  uuid: string;
  platformUuid: string;
  firstName: string;
  middleName: string;
  lastName: string;
  nameSuffix: string;
  gender: string;
  dob: string | null;
  address1: string;
  address2: string;
  countryId: string | null;
  stateId: string | null;
  city: string;
  zip: string;
  altAddress1: string;
  altAddress2: string;
  altCity: string;
  altZip: string;
  phone: string;
  sendTextAlerts: boolean;
  sendTextAlertsDate: string | null;
  email: string;
  emergencyContactName: string;
  emergencyContactPhone: string;
  profileImage: string;
  nickname: string;
  facebook: string;
  snapchat: string;
  twitter: string;
  youtube: string;
  instagram: string;
  linkedIn: string;
  dateCreated: string | null;
  createdByFullName: string;
  dateUpdated: string | null;
  updatedByFullName: string;
  dateDisabled: string | null;
  disabledByFullName: string;
  phoneAreaCode: string;
  emergencyContactPhoneAreaCode: string;
  languageFamilyId: string;
  handed: string;
  heightFeet: string;
  heightInches: string;
  showAgeToPublic: boolean;
  unsubscribeEmarketingPickleball: boolean;
  unsubscribeEmarketingPickleballSms: boolean;
  unsubscribeEmarketing3rdParty: boolean;
  dateClaimed: string | null;
  timezoneId: string | null;
  platforms: {
    id: string;
    name: string;
    platformUserUuid: string;
  }[];
  governingBodies: {
    id: string;
    name: string;
    shortName: string;
    memberNumber: string;
  }[];
  altCountryId: string | null;
  altStateId: string | null;
  totalFollowers: string;
  totalFollowing: string;
  isSubRecord: boolean;
  isTest: boolean;
  isCompleted: boolean;
  emergencyContactPhoneCountryId: string;
  phoneCountryId: string;
  createdForModuleUuid: string;
  selfSkillDoublesId: string | null;
  selfSkillMixedId: string | null;
  selfSkillSinglesId: string | null;
  selfSkillSkinnySinglesId: string | null;
  pickleballUSAPAUTPRSingles: number;
  pickleballUSAPAUTPRSkinnySingles: number;
  pickleballUSAPAUTPRDoubles: number;
  pickleballUSAPAUTPRMixed: number;
  worldPickleballRatingSingles: number;
  worldPickleballRatingDoubles: number;
  worldPickleballRatingMixed: number;
  geniusSportsSinglesUuid: string;
  duprId: string;
  duprDoubles: number;
  duprSingles: number;
  tiktok: string;
  showCityToPublic: boolean;
  showStateToPublic: boolean;
  showOnlyFirstInitialOfLastNameToPublic: boolean;
  proLevelId: string;
  singleRating: number;
  doublesRating: number;
  mixedRating: number;
  skinnySinglesRating: number;
  numberOfPlayedSingleGames: number;
  numberOfPlayedDoublesGames: number;
  numberOfPlayedMixedGames: number;
  numberOfPlayedSkinnySinglesGames: number;
  pls: string;
  bio: string;
  sponsorImg: string;
  featuredImg: string;
  featuredVideo: string;
  representingCountryId: string | null;
  turnedPro: string | null;
  sponsors: UserSponsor[];
  images: UserImage[];
  biography: UserBiography[];
  pickleballUsapaNumber: string;
  pickleballCanadaNumber: string;
  pickleballLifetimeNumber: string;
  pickleballSsipaNumber: string;
  pickleballPaaNumber: string;
  pbUuid: string;
  ratingsReliabilitySingles: number;
  ratingsReliabilitySkinnySingles: number;
  ratingsReliabilityDoubles: number;
  ratingsReliabilityMixed: number;
  slug: string;
  dateMerged: string | null;
  latitude: number;
  longitude: number;
  alternateLatitude: number;
  alternateLongitude: number;
  bodyWeight: string;
  backhand: string;
  coachFirstName: string;
  coachLastName: string;
  lastActiveDate: string | null;
  stripeCustomerId: string;
  duprDateLinkUpdated: string | null;
  socialLoginId: string;
  socialProvider: string | null;
  showMiddleNameToPublic: boolean;
  hideBackToTop: boolean;
  hideQuickLaunchButtons: boolean;
  canLogin: boolean;
  hideOnSearch: boolean;
}

export interface UserImageCreation {
  type: string;
  url: string;
  firstName: string;
  lastName: string;
}

export interface UserBioCreation {
  bioText: string;
  firstName: string;
  lastName: string;
}

export interface UserProducts {
  id: number;
  name: string;
  allowedAccess: boolean;
}

export enum PlayerParticipationType {
  Managing = 'Manage',
  RegisteredFor = 'RegisteredFor',
  Favorited = 'Favorite',
}

export const playerParticipationTypeMap: Record<string, PlayerParticipationType> = {
  registeredfor: PlayerParticipationType.RegisteredFor,
  manage: PlayerParticipationType.Managing,
  favorites: PlayerParticipationType.Favorited,
};

export interface PpaStats {
  startDate: string;
  endDate: string;
  ppaRank: number;
  ppaPoints: number;
  tournamentsNumber: string;
  eventsNumber: string;
  matchOverview: {
    matchesWinNum: number;
    matchesLossNum: number;
    matchesPercentage: number;
  };
  gamesOverview: {
    matchesWinNum: number;
    matchesLossNum: number;
    matchesPercentage: number;
  };
  gameStats: {
    pointsEarned: number;
    pointsAgainst: number;
    difference: number;
    avgOpponentsRating: number | null;
    avgPartnerRating: number | null;
  };
}

export interface PlayerPpaStats {
  seedStats: {
    singles: PpaStats;
    doubles: PpaStats;
    mixedDoubles: PpaStats;
  };
  raceStats: {
    singles: PpaStats;
    doubles: PpaStats;
    mixedDoubles: PpaStats;
  };
}

export enum DuprConnectionAction {
  CONNECT = 'DUPR_CONNECTION_ACTION_CONNECT',
  DISCONNECT = 'DUPR_CONNECTION_ACTION_DISCONNECT',
}

export interface DuprConnectionHistoryItem {
  duprId: string;
  action: DuprConnectionAction;
  performedBy: string;
  createdAt: string;
}

export interface Friend {
  uuid: string;
  firstName: string;
  middleName: string;
  lastName: string;
  nameSuffix: string;
  slug: string;
  fullName: string;
  country: Country;
  state: State;
  city: string;
  profileImage: string;
  friendTime: string;
  gender: string;
  age: number;
  playerLevel: number;
  playerLevelTitle: string;
  handed: string;
  locationLabel: string;
}

export interface PlayerFiltersInterface {
  age: string;
  doubles_skill: string;
  gender: string;
  country_id: string;
  state_id: string;
  city: string;
  mobile_filters: string;
  search: string;
  singles_skill: string;
  wpr_badge: string;
}

export enum FeeIntervalLabelsEnum {
  UNDEFINED = 'Undefined',
  MONTHLY = 'Monthly',
  QUARTERLY = 'Quarterly',
  YEARLY = 'Yearly',
  BI_ANNUALLY = 'Bi-annually',
  ONE_TIME = 'One-time',
  OTHER = 'Other',
}

export const SubscriptionBillingIntervalEnum = {
  UNDEFINED: 'SUBSCRIPTION_BILLING_INTERVAL_UNDEFINED',
  MONTHLY: 'SUBSCRIPTION_BILLING_INTERVAL_MONTHLY',
  QUARTERLY: 'SUBSCRIPTION_BILLING_INTERVAL_QUARTERLY',
  ANNUALLY: 'SUBSCRIPTION_BILLING_INTERVAL_ANNUALLY',
  BI_ANNUALLY: 'SUBSCRIPTION_BILLING_INTERVAL_BI_ANNUALLY',
  NON_RECURRING: 'SUBSCRIPTION_BILLING_INTERVAL_NON_RECURRING',
  OTHER: 'SUBSCRIPTION_BILLING_INTERVAL_OTHER',
} as const;

export type SubscriptionBillingInterval =
  (typeof SubscriptionBillingIntervalEnum)[keyof typeof SubscriptionBillingIntervalEnum];

export const FeeIntervalLabels: Record<
  SubscriptionBillingInterval | number,
  FeeIntervalLabelsEnum
> = {
  0: FeeIntervalLabelsEnum.UNDEFINED,
  1: FeeIntervalLabelsEnum.MONTHLY,
  2: FeeIntervalLabelsEnum.QUARTERLY,
  3: FeeIntervalLabelsEnum.YEARLY,
  4: FeeIntervalLabelsEnum.BI_ANNUALLY,
  5: FeeIntervalLabelsEnum.ONE_TIME,
  6: FeeIntervalLabelsEnum.OTHER,

  SUBSCRIPTION_BILLING_INTERVAL_UNDEFINED: FeeIntervalLabelsEnum.UNDEFINED,
  SUBSCRIPTION_BILLING_INTERVAL_MONTHLY: FeeIntervalLabelsEnum.MONTHLY,
  SUBSCRIPTION_BILLING_INTERVAL_QUARTERLY: FeeIntervalLabelsEnum.QUARTERLY,
  SUBSCRIPTION_BILLING_INTERVAL_ANNUALLY: FeeIntervalLabelsEnum.YEARLY,
  SUBSCRIPTION_BILLING_INTERVAL_BI_ANNUALLY: FeeIntervalLabelsEnum.BI_ANNUALLY,
  SUBSCRIPTION_BILLING_INTERVAL_NON_RECURRING: FeeIntervalLabelsEnum.ONE_TIME,
  SUBSCRIPTION_BILLING_INTERVAL_OTHER: FeeIntervalLabelsEnum.OTHER,
};

export const SubscriptionStatusEnum = {
  UNDEFINED: 'SUBSCRIPTION_STATUS_UNDEFINED',
  ACTIVE: 'SUBSCRIPTION_STATUS_ACTIVE',
  PAYMENT_NEEDED: 'SUBSCRIPTION_STATUS_PAYMENT_NEEDED',
  TRIALING: 'SUBSCRIPTION_STATUS_TRIALING',
  PAUSED: 'SUBSCRIPTION_STATUS_PAUSED',
  CANCELED: 'SUBSCRIPTION_STATUS_CANCELED',
  SCHEDULED: 'SUBSCRIPTION_STATUS_SCHEDULED',
  PAYMENT_POSTPONED: 'SUBSCRIPTION_STATUS_PAYMENT_POSTPONED',
  PENDING_CANCELLATION: 'SUBSCRIPTION_STATUS_PENDING_CANCELLATION',
} as const;

export type SubscriptionStatus =
  (typeof SubscriptionStatusEnum)[keyof typeof SubscriptionStatusEnum];

enum SubscriptionStatusLabelsEnum {
  UNDEFINED = 'Undefined',
  ACTIVE = 'Active',
  PAYMENT_NEEDED = 'Payment Needed',
  TRIALING = 'Trialing',
  PAUSED = 'Paused',
  CANCELED = 'Canceled',
  SCHEDULED = 'Scheduled',
  PAYMENT_POSTPONED = 'Payment Postponed',
  PENDING_CANCELLATION = 'Pending Cancellation',
}

export const SubscriptionStatusLabels: Record<
  SubscriptionStatus | number,
  SubscriptionStatusLabelsEnum
> = {
  0: SubscriptionStatusLabelsEnum.UNDEFINED,
  1: SubscriptionStatusLabelsEnum.ACTIVE,
  2: SubscriptionStatusLabelsEnum.PAYMENT_NEEDED,
  3: SubscriptionStatusLabelsEnum.TRIALING,
  4: SubscriptionStatusLabelsEnum.PAUSED,
  5: SubscriptionStatusLabelsEnum.CANCELED,
  6: SubscriptionStatusLabelsEnum.SCHEDULED,
  7: SubscriptionStatusLabelsEnum.PAYMENT_POSTPONED,
  8: SubscriptionStatusLabelsEnum.PENDING_CANCELLATION,

  SUBSCRIPTION_STATUS_UNDEFINED: SubscriptionStatusLabelsEnum.UNDEFINED,
  SUBSCRIPTION_STATUS_ACTIVE: SubscriptionStatusLabelsEnum.ACTIVE,
  SUBSCRIPTION_STATUS_PAYMENT_NEEDED: SubscriptionStatusLabelsEnum.PAYMENT_NEEDED,
  SUBSCRIPTION_STATUS_TRIALING: SubscriptionStatusLabelsEnum.TRIALING,
  SUBSCRIPTION_STATUS_PAUSED: SubscriptionStatusLabelsEnum.PAUSED,
  SUBSCRIPTION_STATUS_CANCELED: SubscriptionStatusLabelsEnum.CANCELED,
  SUBSCRIPTION_STATUS_SCHEDULED: SubscriptionStatusLabelsEnum.SCHEDULED,
  SUBSCRIPTION_STATUS_PAYMENT_POSTPONED: SubscriptionStatusLabelsEnum.PAYMENT_POSTPONED,
  SUBSCRIPTION_STATUS_PENDING_CANCELLATION: SubscriptionStatusLabelsEnum.PENDING_CANCELLATION,
};

export interface DependentAddOn {
  planUuid: string;
  planTitle: string;
  subscriptionUuid: string;
  status: string;
  datePeriodEnd: string;
  scheduled: boolean;
}
